import { Component, createRef } from 'react';
import styles from '../editor.module.scss';
import { EditState, Tool, Size, Handle, outputSize, orientedSize, upscaleFactor, cropView, resizeView, dragCropHandle, moveCrop, setResizeWidth, setResizeHeight } from '../model/edit-stack';

export type ViewMode = 'fit' | 'actual' | 'original' | 'custom';
export interface Preview { canvas: HTMLCanvasElement; scale: number; }
export interface StageThumb { id: string; name: string; size: Size; thumb: HTMLCanvasElement | null; error?: string; }

export interface StageProps {
    state: EditState;
    tool: Tool;
    view: ViewMode;
    /** Oriented preview (crop + rotate + flip applied, not resized); `scale` = preview px per oriented px. */
    preview: Preview | null;
    /** Untouched source preview for the Original view; `scale` = preview px per source px. */
    sourcePreview: Preview | null;
    /** Crop mode: the image the ops BEFORE the current crop produced — what the crop box sits on (additive model). */
    basePreview: Preview | null;
    /** CSS px per output px (normal view) or per source px (crop / original). */
    zoom: number;
    onZoom: (zoom: number, view: ViewMode) => void;
    fileName: string;
    fileInfo: string;
    onChangeFile: () => void;
    changeLabel: string;
    busy: string | null;
    onState: (next: EditState) => void;
    outputNote: string;
    batch: { items: StageThumb[]; selected: number; onSelect: (i: number) => void; onAddMore: () => void; remaining: number } | null;
    /** Toggle the distraction-free full-window editor; absent = no button (e.g. SSR / iOS). */
    onFullscreen?: () => void;
    isFullscreen?: boolean;
    /** Undo · Redo · Reset trio floating over the photo; shown once there is something to undo or redo. */
    history?: { canUndo: boolean; canRedo: boolean; edited: boolean };
    onUndo?: () => void;
    onRedo?: () => void;
    onResetAll?: () => void;
    /** Drag lifecycle, so the editor can fold a whole handle drag into ONE undo step. */
    onDragging?: (dragging: boolean) => void;
    /** Phone: canvas box height chosen once per photo (px); desktop flexes to fill instead. */
    boxHeight?: number | null;
    /** Thirds grid over the photo while the fine-angle slider is held (UX review N3). */
    showGrid?: boolean;
}

type Drag = { kind: 'crop-handle'; handle: Handle } | { kind: 'crop-move' } | { kind: 'grip'; corner: 'tl' | 'tr' | 'bl' | 'br' };
const SIDE: Record<string, string> = { tl: 'top-left', tr: 'top-right', bl: 'bottom-left', br: 'bottom-right', t: 'top', b: 'bottom', l: 'left', r: 'right' };

/**
 * The photo, the handles, zoom and the filmstrip. Pointer maths lives here; edit maths lives in
 * model/edit-stack.ts. Crop is edited on the source orientation (rotation applies after the crop),
 * so Crop mode shows the untouched source with the box over it.
 */
export class StageComponent extends Component<StageProps, { dragging: boolean; confirmChange: boolean; settling: boolean }> {
    state = { dragging: false, confirmChange: false, settling: false };
    // A corner-grip drag keeps the zoom it started with, so the photo physically grows and shrinks
    // under the finger instead of Fit re-scaling it every frame (owner request 2026-09-06, video);
    // it stays anchored at the OPPOSITE corner while dragging and eases back to Fit, centred, on release.
    private dragZoom: number | null = null;
    private dragStartCss: { w: number; h: number } | null = null;
    private settleTimer: ReturnType<typeof setTimeout> | null = null;
    private wrapRef = createRef<HTMLDivElement>();
    private canvasHost = createRef<HTMLDivElement>();
    private ro: ResizeObserver | null = null;
    private drag: Drag | null = null;
    private dragStart: { x: number; y: number; state: EditState } | null = null;

    componentDidMount() {
        if (typeof ResizeObserver !== 'undefined' && this.wrapRef.current) {
            this.ro = new ResizeObserver(() => this.refit());
            this.ro.observe(this.wrapRef.current);
        }
        // native + non-passive so Ctrl/⌘-wheel can preventDefault the browser's page zoom
        this.wrapRef.current?.addEventListener('wheel', this.onWheel, { passive: false });
        this.refit();
    }
    componentDidUpdate(prev: StageProps) {
        if (prev.view !== this.props.view || prev.tool !== this.props.tool || prev.preview !== this.props.preview || prev.sourcePreview !== this.props.sourcePreview || prev.basePreview !== this.props.basePreview) this.refit();
    }
    componentWillUnmount() { this.ro?.disconnect(); this.wrapRef.current?.removeEventListener('wheel', this.onWheel); if (this.settleTimer) clearTimeout(this.settleTimer); }

    /** Which picture is on screen, and its size in the coordinate system the zoom refers to. */
    private shown(): { preview: Preview | null; logical: Size } {
        const { state, tool, view, preview, sourcePreview, basePreview } = this.props;
        if (view === 'original') return { preview: sourcePreview, logical: state.source };
        // Crop edits the image the ops before it produced (additive model): show that base, in its pixels
        if (tool === 'crop') return { preview: basePreview ?? preview, logical: cropView(state).input };
        return { preview, logical: outputSize(state) };
    }

    /** Scale that fits the image inside the fixed box, never enlarging past 100% (an image smaller
     *  than the box is shown at its own size, centred — not blown up). This is also the zoom FLOOR:
     *  you can never shrink below Fit, so the image and its handles stay reachable in the box and a
     *  stray zoom-slider touch can't collapse it to a dot (the mobile trap, 2026-09-06). */
    private fitZoom(): number {
        const wrap = this.wrapRef.current; if (!wrap) return 1;
        const { logical } = this.shown();
        // Fit leaves ≈½ in (56px) around the photo on phones and ≈1 in (96px) on desktop, so a grip can be
        // dragged OUTWARD to grow the photo (owner, 2026-09-06); the touch zones live inside that gap.
        const pad = window.innerWidth <= 900 ? 112 : 192;
        const w = Math.max(80, wrap.clientWidth - pad), h = Math.max(120, wrap.clientHeight - pad);
        return Math.min(1, w / logical.width, h / logical.height);
    }

    private refit() {
        if (this.props.view === 'fit') {
            const z = this.fitZoom();
            if (Math.abs(z - this.props.zoom) > 1e-4) this.props.onZoom(z, 'fit');
        }
    }
    /** −/+ on phones (UX review U4: a 90px slider is the wrong thumb control): 25% steps, Fit…400%. */
    private stepZoom(dir: 1 | -1) {
        const fit = this.fitZoom(); const z = Math.min(4, Math.max(fit, this.effZoom() * (dir > 0 ? 1.25 : 0.8)));
        this.props.onZoom(z, z <= fit + 1e-3 ? 'fit' : 'custom');
    }

    // ------------------------------------------------------------ pointer handling
    private onPointerDown = (drag: Drag) => (e: React.PointerEvent) => {
        e.preventDefault(); e.stopPropagation();
        (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
        this.drag = drag; this.dragStart = { x: e.clientX, y: e.clientY, state: this.props.state };
        if (drag.kind === 'grip') { this.dragZoom = this.effZoom(); const { logical } = this.shown(); this.dragStartCss = { w: logical.width * this.dragZoom, h: logical.height * this.dragZoom }; }
        this.setState({ dragging: true }); this.props.onDragging?.(true);
    };
    /** Display scale actually on screen. Never below Fit, in any mode: that keeps the image and its
     *  handles inside the box and makes drag deltas sane even if a stale zoom is smaller than Fit. */
    private effZoom(): number {
        if (this.dragZoom !== null) return this.dragZoom;   // frozen for the length of a grip drag
        const fit = this.wrapRef.current ? this.fitZoom() : (this.props.zoom || 1);
        return this.props.view === 'fit' ? fit : Math.max(fit, this.props.zoom);
    }

    /** Shared by pointer drags (deltas already divided by the zoom) and keyboard nudges (whole image
     *  pixels): apply a handle / grip / move delta to a base state. */
    private applyDrag(s0: EditState, d: Drag, dx: number, dy: number): EditState {
        if (d.kind === 'crop-handle') return dragCropHandle(s0, d.handle, dx, dy);
        if (d.kind === 'crop-move') return moveCrop(s0, dx, dy);
        // corner grips change the OUTPUT size; a drag away from the centre grows it
        // the photo on screen IS the output, so the grip moves the current resize's target relative to it
        const out = outputSize(s0);
        const sx = d.corner.includes('l') ? -1 : 1, sy = d.corner.includes('t') ? -1 : 1;
        let next = setResizeWidth(s0, out.width + sx * dx);
        if (!resizeView(s0).lockAspect) next = setResizeHeight(next, out.height + sy * dy);
        return next;
    }
    private onPointerMove = (e: React.PointerEvent) => {
        if (this.trackPinch(e)) return;
        if (!this.drag || !this.dragStart) return;
        const z = Math.max(1e-6, this.effZoom());
        this.props.onState(this.applyDrag(this.dragStart.state, this.drag, (e.clientX - this.dragStart.x) / z, (e.clientY - this.dragStart.y) / z));
    };
    /** Keyboard (UX review K1 — nothing was focusable): arrows nudge the focused handle by one image
     *  pixel, Shift by ten; Escape mid-drag puts everything back to where the drag started. */
    private onKey = (d: Drag) => (e: React.KeyboardEvent) => {
        if (e.key === 'Escape' && this.drag && this.dragStart) {
            this.props.onState(this.dragStart.state); this.drag = null; this.dragStart = null; this.settleFromDrag();
            this.setState({ dragging: false }); this.props.onDragging?.(false); return;
        }
        const step = e.shiftKey ? 10 : 1;
        const v: Record<string, [number, number]> = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] };
        const dv = v[e.key]; if (!dv) return;
        e.preventDefault();
        this.props.onState(this.applyDrag(this.props.state, d, dv[0], dv[1]));
    };

    // ------------------------------------------------------------ zoom gestures (UX review M2)
    // Two fingers on the canvas pinch the PHOTO — before this a pinch zoomed the whole page and
    // broke the fixed box. Ctrl/⌘ + wheel (what a trackpad pinch sends) zooms on desktop; a
    // double-click / double-tap toggles Fit ↔ 100%. The box's touch-action (pan-x pan-y) leaves
    // one-finger scrolling to the browser and hands the second finger to us.
    private pointers = new Map<number, { x: number; y: number }>();
    private pinch: { dist: number; zoom: number } | null = null;
    private onWrapPointerDown = (e: React.PointerEvent) => {
        if (e.pointerType !== 'touch') return;
        this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
        if (this.pointers.size === 2) {
            const [a, b] = [...this.pointers.values()];
            this.pinch = { dist: Math.hypot(a.x - b.x, a.y - b.y) || 1, zoom: this.effZoom() };
            if (this.drag) { this.drag = null; this.dragStart = null; this.setState({ dragging: false }); this.props.onDragging?.(false); }
        }
    };
    /** Returns true while a pinch owns the pointers (the drag logic then stays out of it). */
    private trackPinch(e: React.PointerEvent): boolean {
        if (!this.pointers.has(e.pointerId)) return false;
        this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
        if (!this.pinch || this.pointers.size < 2) return false;
        const [a, b] = [...this.pointers.values()];
        this.setZoom(this.pinch.zoom * (Math.hypot(a.x - b.x, a.y - b.y) || 1) / this.pinch.dist);
        return true;
    }
    private setZoom(z: number) { const fit = this.fitZoom(); const c = Math.min(4, Math.max(fit, z)); this.props.onZoom(c, c <= fit + 1e-3 ? 'fit' : 'custom'); }
    private onWheel = (e: WheelEvent) => { if (!(e.ctrlKey || e.metaKey)) return; e.preventDefault(); this.setZoom(this.effZoom() * (e.deltaY < 0 ? 1.1 : 1 / 1.1)); };
    private onDoubleClick = () => { const fit = this.fitZoom(); if (this.props.view === 'fit' || this.effZoom() <= fit + 1e-3) this.props.onZoom(1, 'actual'); else this.props.onZoom(fit, 'fit'); };
    private onPointerUp = (e: React.PointerEvent) => {
        if (this.drag) { try { (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId); } catch { /* already released */ } }
        this.pointers.delete(e.pointerId); if (this.pointers.size < 2) this.pinch = null;
        this.drag = null; this.dragStart = null; this.settleFromDrag();
        if (this.state.dragging) { this.setState({ dragging: false }); this.props.onDragging?.(false); }
    };

    /** Big, centred read-out of the size while a handle is being dragged — the change is otherwise
     *  easy to miss in the side inputs (owner, 2026-09-06). Resize shows new size + the original;
     *  crop shows the crop rectangle. Anchored to the box (not the image) so it stays centred. */
    private sizeOverlay() {
        if (!this.state.dragging) return null;
        const { state, tool } = this.props;
        if (tool === 'crop') {
            const c = cropView(state).rect;
            return <div className={styles.sizeOverlay} aria-live="polite"><b>{Math.round(c.width)} × {Math.round(c.height)}</b><span>crop</span></div>;
        }
        if (tool === 'resize') {
            const out = outputSize(state); const o = orientedSize(state);   // "from" = the size the resize works from
            const changed = out.width !== o.width || out.height !== o.height;
            // past the original's own detail, say so right here rather than making them look for it
            const up = upscaleFactor(state);
            const sub = up > 1.02 ? `${up.toFixed(up < 10 ? 1 : 0)}× the original` : (changed ? `from ${o.width} × ${o.height}` : '');
            return <div className={styles.sizeOverlay} aria-live="polite"><b>{out.width} × {out.height}</b>{sub && <span>{sub}</span>}</div>;
        }
        return null;
    }

    /** End of a grip drag: let go of the frozen zoom and ease back to Fit, centred (the `.settle`
     *  class carries the transition; it is removed once the ease has finished). */
    private settleFromDrag() {
        if (this.dragZoom === null) return;
        this.dragZoom = null; this.dragStartCss = null;
        this.setState({ settling: true });
        if (this.settleTimer) clearTimeout(this.settleTimer);
        this.settleTimer = setTimeout(() => { this.settleTimer = null; this.setState({ settling: false }); }, 280);
    }
    /** While a grip is dragged the photo is anchored at the OPPOSITE corner: flex centring would
     *  otherwise move both sides half the delta and the grip would slide out from under the finger. */
    private dragAnchorTransform(cssW: number, cssH: number): string | undefined {
        if (this.drag?.kind !== 'grip' || !this.dragStartCss) return undefined;
        const dw = cssW - this.dragStartCss.w, dh = cssH - this.dragStartCss.h;
        const tx = this.drag.corner.includes('l') ? -dw / 2 : dw / 2, ty = this.drag.corner.includes('t') ? -dh / 2 : dh / 2;
        return `translate(${tx}px, ${ty}px)`;
    }

    // ------------------------------------------------------------ render
    render() {
        const { state, tool, view, zoom, busy, batch, fileName, fileInfo, outputNote } = this.props;
        const { preview, logical } = this.shown();
        // In Fit mode drive the display from the fixed box directly, so a large image never renders
        // full-size for a frame before the async zoom lands (the "huge, can't scroll" trap on phones,
        // 2026-09-06). The crop/grip overlays below use this same effZoom, so they stay aligned.
        const effZoom = this.effZoom();
        const cssW = Math.max(1, Math.round(logical.width * effZoom)), cssH = Math.max(1, Math.round(logical.height * effZoom));
        const pct = Math.round(effZoom * 100);
        const cv = cropView(state); const crop = cv.rect;   // in the crop base's pixels (additive model)
        const showCrop = tool === 'crop';
        const showGrips = tool === 'resize' && view !== 'original';
        const h = this.props.history;
        // Fit · 100% · Original and ⛶ live in the FOOT row now, beside the zoom percentage (which is
        // the "shown at" figure) — the head is the file chip alone, so it never wraps to two lines
        // on a phone (UX review M3: the wrapped head cost 85px).
        const viewControls = <div className={styles.view}>
            <div className={styles.seg} role="group" aria-label="Preview scale">
                <button type="button" aria-pressed={view === 'fit'} title="Scale the preview to fit this window" onClick={() => this.props.onZoom(this.fitZoom(), 'fit')}>Fit</button>
                <button type="button" aria-pressed={view === 'actual'} title="One image pixel per screen pixel" onClick={() => this.props.onZoom(1, 'actual')}>100%</button>
                <button type="button" aria-pressed={view === 'original'} title="Show the untouched original" onClick={() => this.props.onZoom(this.props.zoom, view === 'original' ? 'fit' : 'original')}>Original</button>
            </div>
            {this.props.onFullscreen && <button type="button" className={styles.fsbtn} onClick={this.props.onFullscreen}
                aria-pressed={!!this.props.isFullscreen} title={this.props.isFullscreen ? 'Exit full screen' : 'Full screen editor'} aria-label={this.props.isFullscreen ? 'Exit full screen' : 'Full screen editor'}>
                {this.props.isFullscreen
                    ? <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 4v5H4M20 9h-5V4M15 20v-5h5M4 15h5v5" /></svg>
                    : <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" /></svg>}
            </button>}
        </div>;
        return <div className={styles.stage}>
            <div className={styles.stagehead}>
                <div className={styles.filechip}><b title={fileName}>{fileName}</b><span className={styles.dim}>{fileInfo}</span>
                    {/* "Change" is a real button (U2) and, once there are edits, asks inline before throwing them away — no native dialog */}
                    {this.state.confirmChange
                        ? <span className={styles.confirm} role="alertdialog" aria-label="Discard your edits?"><span>Discard edits?</span>
                            <button type="button" className={styles.chg} autoFocus onClick={() => this.setState({ confirmChange: false })}>Keep editing</button>
                            <button type="button" className={`${styles.chg} ${styles.danger}`} onClick={() => { this.setState({ confirmChange: false }); this.props.onChangeFile(); }}>Change photo</button></span>
                        : <button type="button" className={styles.chg} data-testid="change-photo" onClick={() => (h?.edited && this.props.changeLabel === 'Change') ? this.setState({ confirmChange: true }) : this.props.onChangeFile()}>{this.props.changeLabel}</button>}
                </div>
            </div>

            <div className={`${styles.canvaswrap} ${view === 'fit' ? styles.fit : ''}`} ref={this.wrapRef} style={this.props.boxHeight ? ({ '--ed-box': `${this.props.boxHeight}px` } as React.CSSProperties) : undefined}
                onPointerDown={this.onWrapPointerDown} onPointerMove={this.onPointerMove} onPointerUp={this.onPointerUp} onPointerCancel={this.onPointerUp} onDoubleClick={this.onDoubleClick}>
                {/* Undo · Redo · Reset, top-centre, only once there is history; hidden mid-drag so it never sits over the size read-out */}
                {h && (h.edited || h.canRedo) && !this.state.dragging && <div className={styles.trio} role="toolbar" aria-label="Edit history">
                    <button type="button" disabled={!h.canUndo} aria-label="Undo" title="Undo (Ctrl+Z)" onClick={this.props.onUndo}><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 14 4 9l5-5M4 9h10a6 6 0 0 1 0 12h-3" /></svg></button>
                    <button type="button" disabled={!h.canRedo} aria-label="Redo" title="Redo (Ctrl+Shift+Z)" onClick={this.props.onRedo}><svg viewBox="0 0 24 24" aria-hidden="true"><path d="m15 14 5-5-5-5M20 9H10a6 6 0 0 0 0 12h3" /></svg></button>
                    <button type="button" disabled={!h.edited} data-testid="reset-all" title="Back to the original photo" onClick={this.props.onResetAll}>Reset</button>
                </div>}
                <div className={`${styles.photo} ${this.state.settling ? styles.settle : ''}`} ref={this.canvasHost} style={{ width: cssW, height: cssH, transform: this.dragAnchorTransform(cssW, cssH) }} data-testid="stage-photo">
                    {preview && <PreviewCanvas canvas={preview.canvas} />}
                    {this.props.showGrid && <i className={styles.grid} aria-hidden="true" />}
                    {/* every handle is focusable: arrows nudge 1px, Shift+arrows 10px, Escape cancels a drag (K1) */}
                    {/* the <b class=zone> child is the enlarged hit area (24px mouse / 44px touch): a real element,
                        because a ::before box was not being hit-tested at the photo's edge (gesture rig, 2026-09-06) */}
                    {showGrips && (['tl', 'tr', 'bl', 'br'] as const).map(c =>
                        <i key={c} className={`${styles.grip} ${styles[c]}`} role="slider" tabIndex={0} aria-label={`Resize handle, ${SIDE[c]} corner. Arrow keys nudge.`}
                            onPointerDown={this.onPointerDown({ kind: 'grip', corner: c })} onKeyDown={this.onKey({ kind: 'grip', corner: c })}><b className={styles.zone} /></i>)}
                    {showCrop && <div className={styles.cropbox} data-testid="crop-box" role="group" tabIndex={0} aria-label="Crop area. Arrow keys move it."
                        style={{ left: crop.x * effZoom, top: crop.y * effZoom, width: crop.width * effZoom, height: crop.height * effZoom }}
                        onPointerDown={this.onPointerDown({ kind: 'crop-move' })} onKeyDown={this.onKey({ kind: 'crop-move' })}>
                        {cv.shape === 'circle' && <i className={styles.circleMask} aria-hidden="true" />}
                        {(['tl', 'tr', 'bl', 'br'] as const).map(h => <i key={h} className={`${styles.handle} ${styles.c} ${styles[h]}`} role="slider" tabIndex={0} aria-label={`Crop handle, ${SIDE[h]} corner. Arrow keys nudge.`}
                            onPointerDown={this.onPointerDown({ kind: 'crop-handle', handle: h })} onKeyDown={this.onKey({ kind: 'crop-handle', handle: h })}><b className={styles.zone} /></i>)}
                        {(['t', 'b', 'l', 'r'] as const).map(h => <i key={h} className={`${styles.handle} ${styles[h]}`} role="slider" tabIndex={0} aria-label={`Crop handle, ${SIDE[h]} edge. Arrow keys nudge.`}
                            onPointerDown={this.onPointerDown({ kind: 'crop-handle', handle: h })} onKeyDown={this.onKey({ kind: 'crop-handle', handle: h })}><b className={styles.zone} /></i>)}
                    </div>}
                    {busy && <div className={styles.busy}>{busy}</div>}
                </div>
                {this.sizeOverlay()}
            </div>

            {batch && <div className={styles.strip} aria-label="Files in this batch">
                {batch.items.map((it, i) => <button type="button" key={it.id} className={`${styles.thumb} ${i === batch.selected ? styles.sel : ''} ${it.error ? styles.bad : ''}`} title={it.error || it.name} onClick={() => batch.onSelect(i)}>
                    {it.thumb ? <PreviewCanvas canvas={it.thumb} /> : <div style={{ height: 48 }} />}
                    <small>{it.error ? 'failed' : `${it.size.width}×${it.size.height}`}</small>
                </button>)}
                {batch.remaining > 0 && <button type="button" className={`${styles.thumb} ${styles.add}`} onClick={batch.onAddMore}>+ add</button>}
            </div>}

            <div className={styles.stagefoot}>
                <div className={styles.zoom}>
                    <button type="button" className={styles.zbtn} aria-label="Zoom out" onClick={() => this.stepZoom(-1)}>−</button>
                    {/* floor = Fit, so the image can never be shrunk to a dot; the slider bottom IS Fit */}
                    <input type="range" min={Math.max(1, Math.round(this.fitZoom() * 100))} max={400} value={Math.max(Math.round(this.fitZoom() * 100), Math.round(effZoom * 100))} aria-label="Zoom"
                        onChange={e => { const p = +e.target.value / 100; this.props.onZoom(p, p <= this.fitZoom() + 1e-3 ? 'fit' : 'custom'); }} />
                    {/* the percentage IS "shown at"; the full sentence is its tooltip and stays inline only where there is room */}
                    <output title={outputNote} aria-label={`Shown at ${pct}%. ${outputNote}`}>{pct}%</output>
                    <button type="button" className={styles.zbtn} aria-label="Zoom in" onClick={() => this.stepZoom(1)}>+</button>
                </div>
                <span className={styles.previewnote}>{outputNote}</span>
                {viewControls}
            </div>
        </div>;
    }
}

/** Mounts a canvas produced by the renderer without copying its pixels. */
class PreviewCanvas extends Component<{ canvas: HTMLCanvasElement }> {
    private host = createRef<HTMLDivElement>();
    componentDidMount() { this.attach(); }
    componentDidUpdate() { this.attach(); }
    private attach() {
        const h = this.host.current; if (!h) return;
        if (h.firstChild !== this.props.canvas) { while (h.firstChild) h.removeChild(h.firstChild); h.appendChild(this.props.canvas); }
    }
    render() { return <div ref={this.host} style={{ width: '100%', height: '100%' }} />; }
}
